/**
 * Phase 19 — Factory für Root-Dokument-Aggregate (ein Dokument pro Business, wie stockData).
 */

import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { isEntityStoreAuthorityEnabled } from '../infrastructure/storage/entityAggregateStore';
import { readBestBusinessMergedPayload } from './sharedCore';

/**
 * @param {object} config
 * @param {string} config.field
 * @param {(businessId: string) => Promise<object>} config.getEntity
 * @param {(businessId: string) => Promise<object>} [config.getAggregate]
 * @param {object} [config.emptyValue]
 * @param {(doc: object) => boolean} [config.hasContent]
 */
export function createRootDocumentEntityReadSource({
  field,
  getEntity,
  getAggregate,
  emptyValue = {},
  hasContent = (doc) => doc && typeof doc === 'object' && Object.keys(doc).length > 0
}) {
  /** @type {Map<string, object>} */
  const cache = new Map();

  function readFromMergedBlob(businessId) {
    if (!businessId) return emptyValue;
    if (isEntityStoreNoKvBlobEnabled() || isEntityStoreAuthorityEnabled()) {
      const cached = getCachedRead(businessId);
      if (cached != null && hasContent(cached)) return cached;
    }
    if (isEntityStoreNoKvBlobEnabled()) return emptyValue;
    const { parsed } = readBestBusinessMergedPayload(businessId);
    const value = parsed?.[field];
    if (value == null) return emptyValue;
    return typeof value === 'object' ? value : emptyValue;
  }

  async function readFromBestSource(businessId) {
    if (!businessId) return emptyValue;

    if (isEntityStoreNoKvBlobEnabled()) {
      try {
        const fromEntity = await getEntity(businessId);
        if (fromEntity && hasContent(fromEntity)) return fromEntity;
      } catch {
        /* ignore */
      }
      return emptyValue;
    }

    if (isEntityStoreAuthorityEnabled()) {
      try {
        const fromEntity = await getEntity(businessId);
        if (fromEntity && hasContent(fromEntity)) {
          return fromEntity;
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[${field}ReadSource] entity read failed:`, err?.message || err);
        }
      }
    }

    const fromBlob = (() => {
      if (isEntityStoreNoKvBlobEnabled()) return emptyValue;
      const { parsed } = readBestBusinessMergedPayload(businessId);
      const value = parsed?.[field];
      if (value == null || typeof value !== 'object') return emptyValue;
      return value;
    })();
    if (hasContent(fromBlob)) return fromBlob;

    if (getAggregate) {
      try {
        const fromAggregate = await getAggregate(businessId);
        if (fromAggregate && hasContent(fromAggregate)) {
          return fromAggregate;
        }
      } catch {
        /* ignore */
      }
    }

    try {
      const fromEntity = await getEntity(businessId);
      if (fromEntity && hasContent(fromEntity)) return fromEntity;
    } catch {
      /* ignore */
    }

    return fromBlob;
  }

  async function refreshReadCache(businessId, preloaded) {
    const doc =
      preloaded !== undefined
        ? preloaded && typeof preloaded === 'object'
          ? preloaded
          : emptyValue
        : await readFromBestSource(businessId);
    if (businessId) {
      cache.set(String(businessId), doc);
    }
    return doc;
  }

  function getCachedRead(businessId) {
    if (!businessId) return null;
    const cached = cache.get(String(businessId));
    return cached != null ? cached : null;
  }

  function clearReadCache(businessId) {
    if (businessId) {
      cache.delete(String(businessId));
    } else {
      cache.clear();
    }
  }

  function readSyncForBusiness(businessId) {
    if (!businessId) return emptyValue;
    const cached = getCachedRead(businessId);
    if (cached) return cached;
    return readFromMergedBlob(businessId);
  }

  return {
    readFromMergedBlob,
    readFromBestSource,
    refreshReadCache,
    getCachedRead,
    clearReadCache,
    readSyncForBusiness
  };
}
